import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { updateUserEducation, type IUpdateUserEducationApiPayload } from '~/domain/UpdateOffice';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IUserCredentials } from '~/models/UserCredentials';
import { apiMethodsConst } from '~/services/methods';
import { applyCredentialsPatch } from '../payloads/buildCredentialsPatch';
import { locationSettingsKeys } from './locationSettingsKeys';

/**
 * `PATCH /credential-service/user/credentials` for one location, with its own
 * `x-office`. `state.userCredentials` belongs to the office the app is scoped
 * to, so it is replaced only when that is the location saved.
 */
export const useUpdateLocationCredentials = (officeId: string) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const scopedOfficeId = useAppSelector(
    (state) => (state.user.selectedChildOffice ?? state.user.user)?.id,
  );

  return useMutation<unknown, unknown, IUpdateUserEducationApiPayload>({
    mutationFn: (patch) => updateUserEducation(patch, { officeId }),
    onSuccess: (_response, patch) => {
      const key = locationSettingsKeys.credentials(officeId);
      const next = applyCredentialsPatch(queryClient.getQueryData<IUserCredentials>(key), patch);
      queryClient.setQueryData(key, next);
      queryClient.invalidateQueries({ queryKey: key });

      if (officeId === scopedOfficeId) {
        dispatch({ type: apiMethodsConst.USER_CREDENTIALS_REQUEST, payload: next });
      }
    },
    meta: { skipGlobalErrorToaster: true },
  });
};
